import { ComponentType } from 'react';
import type { BackgroundThemeId } from '../../types';
import { FallingCometsBackground } from './themes/FallingCometsBackground';
import { GlowingStarRainBackground } from './themes/GlowingStarRainBackground';
import { MatrixRainBackground } from './themes/MatrixRainBackground';
import { PetalRainBackground } from './themes/PetalRainBackground';
import { SnowfallBackground } from './themes/SnowfallBackground';

export interface BackgroundThemeProps {
  tone: 'light' | 'dark';
  activity: 'normal' | 'low';
}

export interface BackgroundThemeDefinition {
  id: BackgroundThemeId;
  labelKey: string;
  component?: ComponentType<BackgroundThemeProps>;
  previewClassName: string;
}

export const DEFAULT_BACKGROUND_THEME: BackgroundThemeId = 'none';
export const BACKGROUND_THEME_INTENSITY = { min: 10, max: 100, step: 5, default: 60 } as const;

export const BACKGROUND_THEMES: BackgroundThemeDefinition[] = [
  {
    id: 'none',
    labelKey: 'settings.appearance.backgroundThemes.none',
    previewClassName: 'bg-slate-100 dark:bg-slate-800',
  },
  {
    id: 'matrix',
    labelKey: 'settings.appearance.backgroundThemes.matrix',
    component: MatrixRainBackground,
    previewClassName:
      'bg-[linear-gradient(135deg,rgba(13,148,136,0.95),rgba(15,23,42,0.86)),repeating-linear-gradient(90deg,rgba(45,212,191,0.5)_0_2px,transparent_2px_14px)]',
  },
  {
    id: 'petals',
    labelKey: 'settings.appearance.backgroundThemes.petals',
    component: PetalRainBackground,
    previewClassName:
      'bg-[radial-gradient(circle_at_28%_24%,rgba(244,114,182,0.86)_0_9%,transparent_10%),radial-gradient(circle_at_72%_35%,rgba(251,207,232,0.86)_0_8%,transparent_9%),radial-gradient(circle_at_48%_70%,rgba(236,72,153,0.72)_0_10%,transparent_11%),linear-gradient(135deg,rgba(255,241,242,0.98),rgba(252,231,243,0.96))]',
  },
  {
    id: 'comets',
    labelKey: 'settings.appearance.backgroundThemes.comets',
    component: FallingCometsBackground,
    previewClassName:
      'bg-[radial-gradient(circle_at_18%_28%,rgba(255,255,255,0.9)_0_1px,transparent_2px),radial-gradient(circle_at_38%_18%,rgba(255,255,255,0.76)_0_1px,transparent_2px),radial-gradient(circle_at_62%_48%,rgba(255,255,255,0.82)_0_1px,transparent_2px),radial-gradient(circle_at_82%_30%,rgba(255,255,255,0.72)_0_1px,transparent_2px),linear-gradient(135deg,transparent_47%,rgba(224,242,254,0.95)_48%,rgba(125,211,252,0)_53%),linear-gradient(135deg,rgba(15,23,42,0.98),rgba(49,46,129,0.94))]',
  },
  {
    id: 'stars',
    labelKey: 'settings.appearance.backgroundThemes.stars',
    component: GlowingStarRainBackground,
    previewClassName:
      'bg-[radial-gradient(circle_at_24%_72%,rgba(254,243,199,0.96)_0_3px,rgba(250,204,21,0.46)_4px,transparent_11px),radial-gradient(circle_at_48%_40%,rgba(254,243,199,0.96)_0_2px,rgba(250,204,21,0.42)_3px,transparent_9px),radial-gradient(circle_at_70%_64%,rgba(254,243,199,0.96)_0_3px,rgba(250,204,21,0.4)_4px,transparent_12px),radial-gradient(circle_at_82%_26%,rgba(254,243,199,0.92)_0_2px,rgba(250,204,21,0.36)_3px,transparent_8px),linear-gradient(180deg,rgba(250,204,21,0)_8%,rgba(250,204,21,0.28)_32%,rgba(250,204,21,0)_58%),linear-gradient(180deg,rgba(254,243,199,0)_0%,rgba(254,243,199,0.22)_30%,rgba(254,243,199,0)_55%),linear-gradient(135deg,rgba(2,6,23,0.98),rgba(15,23,42,0.96))]',
  },
  {
    id: 'snow',
    labelKey: 'settings.appearance.backgroundThemes.snow',
    component: SnowfallBackground,
    previewClassName:
      'bg-[radial-gradient(circle_at_18%_28%,rgba(255,255,255,0.92)_0_2px,transparent_3px),radial-gradient(circle_at_34%_70%,rgba(224,242,254,0.86)_0_1px,transparent_3px),radial-gradient(circle_at_58%_38%,rgba(255,255,255,0.88)_0_3px,transparent_5px),radial-gradient(circle_at_76%_62%,rgba(224,242,254,0.78)_0_2px,transparent_4px),radial-gradient(circle_at_88%_24%,rgba(255,255,255,0.74)_0_1px,transparent_3px),linear-gradient(135deg,rgba(30,58,138,0.96),rgba(15,23,42,0.96))]',
  },
];

export function resolveBackgroundTheme(value: unknown): BackgroundThemeDefinition {
  return (
    BACKGROUND_THEMES.find((theme) => theme.id === value) ??
    BACKGROUND_THEMES.find((theme) => theme.id === DEFAULT_BACKGROUND_THEME) ??
    BACKGROUND_THEMES[0]
  );
}

export function clampIntensity(value: unknown): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return BACKGROUND_THEME_INTENSITY.default;
  }

  const rounded =
    Math.round(value / BACKGROUND_THEME_INTENSITY.step) * BACKGROUND_THEME_INTENSITY.step;
  return Math.min(
    BACKGROUND_THEME_INTENSITY.max,
    Math.max(BACKGROUND_THEME_INTENSITY.min, rounded)
  );
}

export function intensityToOpacity(intensity: number): number {
  const clamped = clampIntensity(intensity);
  const progress =
    (clamped - BACKGROUND_THEME_INTENSITY.min) /
    (BACKGROUND_THEME_INTENSITY.max - BACKGROUND_THEME_INTENSITY.min);

  return 0.15 + progress * 0.85;
}
